// src/app/services/token.service.ts
import { Injectable } from '@angular/core';
import { getAuth, User } from 'firebase/auth';

/**
 * Servicio centralizado para gestión de tokens
 * ÚNICA fuente de verdad para todos los tokens de autenticación
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
  constructor() {}
  
  // ============================================
  // FIREBASE CUSTOM TOKEN (solo para login inicial)
  // ============================================
  
  /**
   * Guarda el Firebase Custom Token recibido del backend
   * Este token solo se usa una vez para autenticarse en Firebase
   */
  setFirebaseCustomToken(token: string): void {
    if (!token) {
      console.warn('⚠️ Intentando guardar firebaseCustomToken vacío');
      return;
    }
    localStorage.setItem('firebaseCustomToken', token);
    console.log('✅ Firebase Custom Token guardado');
  }
  
  /**
   * Obtiene el Firebase Custom Token
   */
  getFirebaseCustomToken(): string | null {
    return localStorage.getItem('firebaseCustomToken');
  }
  
  // ============================================
  // FIREBASE ID TOKEN (para APIs nuevas)
  // ============================================
  
  /**
   * Obtiene el Firebase ID Token del usuario actual
   * Este token se usa para autenticarse en APIs nuevas (/api/*)
   * 
   * @param forceRefresh Si es true, fuerza la renovación del token
   * @returns Promise con el ID Token o null si no hay usuario
   */
  async getFirebaseIdToken(forceRefresh: boolean = false): Promise<string | null> {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      console.warn('⚠️ No hay usuario en Firebase Auth');
      return null;
    }
    
    try {
      const token = await user.getIdToken(forceRefresh);
      return token;
    } catch (error) {
      console.error('❌ Error obteniendo Firebase ID Token:', error);
      return null;
    }
  }
  
  // ============================================
  // BACKEND TOKEN (para APIs viejas)
  // ============================================
  
  /**
   * Guarda el token del backend (authCoreToken)
   * Este token se usa para autenticarse en APIs viejas (sin /api/)
   * También se guarda en cookie para compatibilidad
   */
  setBackendToken(token: string): void {
    if (!token) {
      console.warn('⚠️ Intentando guardar backendToken vacío');
      return;
    }
    
    localStorage.setItem('authCoreToken', token);
    this.setCookie('authCoreKey', token, 5); // 5 días
    console.log('✅ Backend Token guardado');
  }
  
  /**
   * Obtiene el token del backend
   * Prioridad: localStorage > cookie
   * 
   * ⚠️ IMPORTANTE: NO usar dataJwt como fallback (es un JWT largo)
   */
  getBackendToken(): string | null {
    // Prioridad 1: localStorage
    const token = localStorage.getItem('authCoreToken');
    
    if (token) {
      return token;
    }
    
    // Prioridad 2: cookie
    const cookieToken = this.getCookie('authCoreKey');
    if (cookieToken) {
      return cookieToken;
    }
    
    return null;
  }
  
  /**
   * Actualiza el token del backend
   * Útil después de sessionLogin
   */
  updateBackendToken(token: string): void {
    this.setBackendToken(token);
  }
  
  // ============================================
  // DATA JWT (solo lectura, NO para autenticación)
  // ============================================
  
  /**
   * Guarda el JWT con datos del usuario
   * Este token NO se usa para autenticación, solo para lectura de datos
   */
  setDataJwt(jwt: string): void {
    if (!jwt) return;
    localStorage.setItem('dataJwt', jwt);
  }
  
  /**
   * Obtiene el JWT con datos del usuario
   * ⚠️ NO usar para autenticación
   */
  getDataJwt(): string | null {
    return localStorage.getItem('dataJwt');
  }
  
  // ============================================
  // LIMPIEZA
  // ============================================
  
  /**
   * Limpia TODOS los tokens de autenticación
   * NO borra otros datos del localStorage
   */
  clearAllTokens(): void {
    const hadToken = !!localStorage.getItem('authCoreToken');
    
    localStorage.removeItem('firebaseCustomToken');
    localStorage.removeItem('authCoreToken');
    localStorage.removeItem('dataJwt');
    this.eraseCookie('authCoreKey');
    
    console.log('✅ Todos los tokens limpiados');
  }
  
  // ============================================
  // HELPERS (cookies)
  // ============================================
  
  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }
  
  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }
  
  private eraseCookie(name: string): void {
    document.cookie = `${name}=; Max-Age=0; path=/`;
  }
}

